import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LockKey, Eye, EyeSlash, CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-10 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-950/30 mb-6">
            <WarningCircle size={40} weight="duotone" className="text-red-600" />
          </div>
          <h2 className="text-3xl mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>
            Invalid link
          </h2>
          <p className="text-muted-foreground text-sm mb-6">This reset link is missing a token. Please request a new one.</p>
          <Link to="/forgot-password" className="inline-block bg-primary text-primary-foreground rounded-full px-6 py-3 font-medium hover:opacity-90">
            Request new link
          </Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, password });
      setDone(true);
      toast.success('Password reset successful. Please sign in.');
      setTimeout(() => navigate('/login'), 1800);
    } catch (err) {
      setError(err.response?.data?.detail || 'Reset failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="max-w-md w-full bg-card border border-border rounded-lg p-10 text-center" data-testid="reset-success">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-950/30 mb-6">
            <CheckCircle size={40} weight="duotone" className="text-green-600" />
          </div>
          <h2 className="text-3xl mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>
            Password reset
          </h2>
          <p className="text-muted-foreground text-sm mb-6">Redirecting you to sign in…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md">
        <div className="bg-card border border-border rounded-lg p-10 shadow-sm">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
              <LockKey size={32} weight="duotone" className="text-primary" />
            </div>
            <h1 className="text-3xl md:text-4xl tracking-tight mb-2" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>
              Set new password
            </h1>
            <p className="text-muted-foreground text-sm">Choose a password with at least 8 characters.</p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">New password</label>
              <div className="relative">
                <LockKey size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type={showPwd ? 'text' : 'password'}
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="reset-password-input"
                  className="w-full pl-9 pr-10 py-3 text-sm bg-muted/40 border border-transparent focus:border-primary focus:bg-background rounded-lg outline-none"
                />
                <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" tabIndex={-1}>
                  {showPwd ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Confirm password</label>
              <input
                type={showPwd ? 'text' : 'password'}
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-confirm-input"
                className="w-full px-3 py-3 text-sm bg-muted/40 border border-transparent focus:border-primary focus:bg-background rounded-lg outline-none"
              />
            </div>

            {error && (
              <div data-testid="reset-error" className="text-sm text-red-600 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="reset-submit"
              className="w-full bg-primary text-primary-foreground rounded-full py-3 font-semibold hover:opacity-90 disabled:opacity-60"
            >
              {busy ? 'Saving…' : 'Reset password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
